// Queries and mutations of cloud's notification destinations. Keys are written
// here only.
//
// - pushQuery: the server's view (the VAPID key, every destination of the person).
// - browserPushQuery: this browser's permission and subscription. Not a fetch,
//   but kept in the cache like one (and never in an effect). It only changes
//   through the mutations here, so it is never stale on its own.
// - appNoticesQuery: inside the iOS app, how its notifications stand (asked
//   of the app; what the app says on its own is put in by the background).
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PushState,
  PushSubscriptionView,
} from "@trameria/cloud-api/notices-shared";
import {
  type Api,
  type AppNoticeHost,
  optimisticMutation,
  readAppNotices,
  type ShellNoticesState,
  useApi,
} from "@trameria/ui-core";
import { useCallback } from "react";
import {
  type BrowserPushState,
  disableApp,
  disableDesktop,
  enableApp,
  enableDesktop,
  readBrowserPush,
  registerApp,
  registerDesktop,
  restoreLostSubscription,
  subscribeBrowser,
  unsubscribeBrowser,
} from "./push";

export const pushKey = ["me", "push"] as const;

type Destination = Pick<PushSubscriptionView, "id" | "kind">;
const browserPushKey = [...pushKey, "browser"] as const;
const appNoticesKey = [...pushKey, "app"] as const;

export const pushQuery = (api: Api) =>
  queryOptions({
    queryKey: pushKey,
    queryFn: () => api<PushState>("/api/me/push"),
  });

export const browserPushQuery = () =>
  queryOptions({
    queryKey: browserPushKey,
    queryFn: () => readBrowserPush(),
    staleTime: Number.POSITIVE_INFINITY,
  });

/** Reads the server's list again (and, by prefix, this browser's state). */
function useInvalidatePush() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: pushKey });
}

/**
 * After subscribing, this browser's state is what subscribing returned, not
 * read again: right after the first permission an iPhone home screen app
 * answered "no subscription" and the switch went back off.
 */
function useApplySubscribed() {
  const qc = useQueryClient();
  return (endpoint: string) => {
    const subscribed: BrowserPushState = { kind: "subscribed", endpoint };
    qc.setQueryData(browserPushQuery().queryKey, subscribed);
    void qc.invalidateQueries({ queryKey: pushKey, exact: true });
  };
}

export function useSubscribeBrowser() {
  const api = useApi();
  const invalidate = useInvalidatePush();
  const applySubscribed = useApplySubscribed();
  return useMutation({
    mutationFn: (publicKey: string) => subscribeBrowser(api, publicKey),
    onSuccess: applySubscribed,
    onError: () => void invalidate(),
  });
}

export function useUnsubscribeBrowser() {
  const api = useApi();
  const invalidate = useInvalidatePush();
  return useMutation({
    mutationFn: () => unsubscribeBrowser(api),
    onSettled: () => void invalidate(),
  });
}

export function useRestoreSubscription() {
  const api = useApi();
  const applySubscribed = useApplySubscribed();
  return useMutation({
    mutationFn: (publicKey: string) => restoreLostSubscription(api, publicKey),
    onSuccess: (endpoint) => {
      if (endpoint) applySubscribed(endpoint);
    },
  });
}

export function useRegisterDesktop() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (installationId: string) =>
      registerDesktop(api, installationId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: pushKey, exact: true }),
  });
}

export function useSetDesktop() {
  const api = useApi();
  const invalidate = useInvalidatePush();
  return useMutation({
    mutationFn: (input: { installationId: string; on: boolean }) =>
      input.on
        ? enableDesktop(api, input.installationId).then(() => undefined)
        : disableDesktop(api, input.installationId),
    onSettled: () => void invalidate(),
  });
}

/** A test notice to one destination. */
export function useSendTest() {
  const api = useApi();
  const invalidate = useInvalidatePush();
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      api<{ sent: number }>("/api/me/push/test", {
        method: "POST",
        body: { subscriptionId },
      }),
    onSuccess: () => void invalidate(),
  });
}

/** Removes a destination (another browser, a desktop, or an app). */
export function useRemoveDestination() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (row: Destination) =>
      api(
        `/api/me/push/${
          row.kind === "desktop"
            ? "desktop"
            : row.kind === "app"
              ? "app"
              : "subscriptions"
        }/${encodeURIComponent(row.id)}`,
        { method: "DELETE" },
      ),
    ...optimisticMutation<PushState, Destination>(qc, pushKey, (old, row) =>
      old
        ? {
            ...old,
            subscriptions: old.subscriptions.filter((s) => s.id !== row.id),
          }
        : old,
    ),
  });
}

// ---- the iOS app ------------------------------------------------------------------

export const appNoticesQuery = (host: AppNoticeHost) =>
  queryOptions({
    queryKey: appNoticesKey,
    queryFn: () => readAppNotices(host),
    staleTime: Number.POSITIVE_INFINITY,
  });

/** What the app said on its own (the person came back from its settings). */
export function useApplyAppNotices() {
  const qc = useQueryClient();
  return useCallback(
    (state: ShellNoticesState) => void qc.setQueryData(appNoticesKey, state),
    [qc],
  );
}

export function useRegisterApp(host: AppNoticeHost) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (state: ShellNoticesState) => registerApp(api, host, state),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: pushKey, exact: true }),
  });
}

export function useSetApp(host: AppNoticeHost) {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useInvalidatePush();
  return useMutation({
    mutationFn: async (on: boolean) => {
      if (!on) {
        await disableApp(api, host.notices.installationId);
        return null;
      }
      const state = await enableApp(api, host);
      if (state) qc.setQueryData(appNoticesKey, state);
      return state;
    },
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: pushKey, exact: true }),
    onError: () => void invalidate(),
  });
}

export function usePushState() {
  return useQuery(pushQuery(useApi()));
}
